import React, { useState, useEffect } from 'react';
import SolutionSearch from '../components/AISolutions/SolutionSearch';
import SolutionCategories from '../components/AISolutions/SolutionCategories';
import SolutionsList from '../components/AISolutions/SolutionsList';
import { AISolution, API_CONFIG } from '../config/api';
import './AISolutions.css';

const AISolutions: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [solutions, setSolutions] = useState<AISolution[]>([]);
  const [allSolutions, setAllSolutions] = useState<AISolution[]>([]);
  const [filteredSolutions, setFilteredSolutions] = useState<AISolution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSolutions, setTotalSolutions] = useState(0);
  const solutionsPerPage = 100;

  useEffect(() => {
    fetchSolutions(1);
    fetchAllSolutions();
  }, []);

  const fetchAllSolutions = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/ai-solutions?limit=1000`);
      const data = await response.json();
      
      if (data.data) {
        const transformedSolutions = data.data.map((solution: any) => ({
          ...solution,
          industry_focus: typeof solution.industry_focus === 'string' ? JSON.parse(solution.industry_focus || '[]') : (solution.industry_focus || []),
          use_cases: typeof solution.use_cases === 'string' ? JSON.parse(solution.use_cases || '[]') : (solution.use_cases || []),
          integration_types: typeof solution.integration_types === 'string' ? JSON.parse(solution.integration_types || '[]') : (solution.integration_types || []),
          supported_platforms: typeof solution.supported_platforms === 'string' ? JSON.parse(solution.supported_platforms || '[]') : (solution.supported_platforms || []),
          features: typeof solution.features === 'string' ? JSON.parse(solution.features || '[]') : (solution.features || []),
          benefits: typeof solution.benefits === 'string' ? JSON.parse(solution.benefits || '[]') : (solution.benefits || []),
          keywords: typeof solution.keywords === 'string' ? JSON.parse(solution.keywords || '[]') : (solution.keywords || []),
          tags: typeof solution.tags === 'string' ? JSON.parse(solution.tags || '[]') : (solution.tags || []),
          company_website: solution.company_website || solution.website_url,
          pricing_details: solution.pricing_details || '',
          demo_url: solution.demo_url || '',
          documentation_url: solution.documentation_url || ''
        }));
        setAllSolutions(transformedSolutions);
      }
    } catch (error) {
      console.error('Error fetching all solutions:', error);
    }
  };

  const fetchSolutions = async (page = 1) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/ai-solutions?page=${page}&limit=${solutionsPerPage}`);
      const data = await response.json();
      
      if (data.data) {
        // Transform API response to match AISolution interface
        const transformedSolutions = data.data.map((solution: any) => ({
          ...solution,
          industry_focus: typeof solution.industry_focus === 'string' ? JSON.parse(solution.industry_focus || '[]') : (solution.industry_focus || []),
          use_cases: typeof solution.use_cases === 'string' ? JSON.parse(solution.use_cases || '[]') : (solution.use_cases || []),
          integration_types: typeof solution.integration_types === 'string' ? JSON.parse(solution.integration_types || '[]') : (solution.integration_types || []),
          supported_platforms: typeof solution.supported_platforms === 'string' ? JSON.parse(solution.supported_platforms || '[]') : (solution.supported_platforms || []),
          features: typeof solution.features === 'string' ? JSON.parse(solution.features || '[]') : (solution.features || []),
          benefits: typeof solution.benefits === 'string' ? JSON.parse(solution.benefits || '[]') : (solution.benefits || []),
          keywords: typeof solution.keywords === 'string' ? JSON.parse(solution.keywords || '[]') : (solution.keywords || []),
          tags: typeof solution.tags === 'string' ? JSON.parse(solution.tags || '[]') : (solution.tags || []),
          company_website: solution.company_website || solution.website_url,
          pricing_details: solution.pricing_details || '',
          demo_url: solution.demo_url || '',
          documentation_url: solution.documentation_url || ''
        }));
        setSolutions(transformedSolutions);
        setTotalSolutions(data.pagination?.totalItems || transformedSolutions.length);
      }
    } catch (error) {
      console.error('Error fetching AI solutions:', error);
      setError('Failed to load AI solutions');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchSolutions(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(totalSolutions / solutionsPerPage);

  useEffect(() => {
    // Filter solutions based on search term and categories
    let filtered = solutions;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(solution =>
        solution.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        solution.short_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        solution.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        solution.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by categories
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(solution =>
        selectedCategories.includes(solution.category)
      );
    }

    setFilteredSolutions(filtered);
  }, [solutions, searchTerm, selectedCategories]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleCategoryChange = (categories: string[]) => {
    setSelectedCategories(categories);
  };

  // Generate categories from all solutions (not just current page)
  const categories = allSolutions.reduce((acc: any[], solution) => {
    const existing = acc.find(cat => cat.name === solution.category);
    if (existing) {
      existing.count++;
    } else {
      acc.push({ name: solution.category, count: 1 });
    }
    return acc;
  }, []);

  if (loading) {
    return (
      <div className="ai-solutions">
        <div className="ai-solutions__container">
          <div className="loading-message">Loading AI solutions...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-solutions">
        <div className="ai-solutions__container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-solutions">
      <div className="ai-solutions__container">
        <div className="ai-solutions__header">
          <h1>AI Solutions Directory</h1>
          <p>Discover and explore cutting-edge AI tools and platforms to transform your business operations.</p>
        </div>

        <div className="ai-solutions__search">
          <SolutionSearch 
            searchTerm={searchTerm}
            onSearch={handleSearch} 
          />
        </div>

        <div className="ai-solutions__main">
          <div className="ai-solutions__sidebar">
            <SolutionCategories 
              categories={categories}
              selectedCategories={selectedCategories}
              onCategoryChange={handleCategoryChange}
            />
          </div>

          <div className="ai-solutions__content">
            <SolutionsList 
              solutions={filteredSolutions}
            />
            
            {totalPages > 1 && (
              <div className="ai-solutions__pagination">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn pagination-btn--prev"
                >
                  ← Previous
                </button>
                
                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`pagination-btn ${currentPage === page ? 'pagination-btn--active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn pagination-btn--next"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISolutions;
